/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { Button, Dialog, Tabs, Typography } from '@equinor/eds-core-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  getApiV1ImagesVariogramByImageId,
  GetVariogramResultsVariogramResultFileDto,
} from '../../../../../api/generated';
import { CenteredLoader } from '../../../../../components/CenteredLoader/CenteredLoader';
import { queryKeys } from '../../../../../hooks/queryKeys';
import * as Styled from './ImageResult.styled';

function useBlobUrl(data: unknown): string {
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (data instanceof Blob) {
      const objectUrl = URL.createObjectURL(data);
      setUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    setUrl('');
  }, [data]);

  return url;
}

export const ImageResult = ({
  open,
  setOpen,
  resultImages,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resultImages: GetVariogramResultsVariogramResultFileDto[];
}) => {
  const [activeTab, setActiveTab] = useState<number | string>(0);
  const handleChange = (index: string | number) => {
    setActiveTab(index);
  };

  const variogramSlices =
    resultImages &&
    resultImages.find((x) => x.fileName.includes('variogram_slices_'));
  const variogramSlicesImageId =
    variogramSlices && variogramSlices.variogramResultFileId;

  const loadedVariogramSlicesImage = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: queryKeys.caseImage(variogramSlicesImageId as string),
    queryFn: () =>
      getApiV1ImagesVariogramByImageId({
        path: { imageId: variogramSlicesImageId as string },
      }),
    enabled: !!variogramSlicesImageId,
    staleTime: Infinity,
  });

  const sphericalImage =
    resultImages && resultImages.find((x) => x.fileName.includes('spherical-'));
  const sphericalImageId =
    sphericalImage && sphericalImage.variogramResultFileId;

  const loadedSphericalImage = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: queryKeys.caseImage(sphericalImageId as string),
    queryFn: () =>
      getApiV1ImagesVariogramByImageId({
        path: { imageId: sphericalImageId as string },
      }),
    enabled: !!sphericalImageId,
    staleTime: Infinity,
  });

  const gaussianImage =
    resultImages && resultImages.find((x) => x.fileName.includes('gaussian'));
  const gaussianImageId = gaussianImage && gaussianImage.variogramResultFileId;
  const loadedGaussianImage = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: queryKeys.caseImage(gaussianImageId as string),
    queryFn: () =>
      getApiV1ImagesVariogramByImageId({
        path: { imageId: gaussianImageId as string },
      }),
    enabled: !!gaussianImageId,
    staleTime: Infinity,
  });

  const generalExponentialImage =
    resultImages &&
    resultImages.find((x) => x.fileName.includes('general_exponential'));
  const generalExponentialImageId =
    generalExponentialImage && generalExponentialImage.variogramResultFileId;
  const loadedGeneralExponentialImage = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: queryKeys.caseImage(generalExponentialImageId as string),
    queryFn: () =>
      getApiV1ImagesVariogramByImageId({
        path: { imageId: generalExponentialImageId as string },
      }),
    enabled: !!generalExponentialImageId,
    staleTime: Infinity,
  });

  const exponentialImage =
    resultImages &&
    resultImages.find((x) => x.fileName.includes('-exponential'));
  const exponentialImageId =
    exponentialImage && exponentialImage.variogramResultFileId;
  const loadedExponentialImage = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: queryKeys.caseImage(exponentialImageId as string),
    queryFn: () =>
      getApiV1ImagesVariogramByImageId({
        path: { imageId: exponentialImageId as string },
      }),
    enabled: !!exponentialImageId,
    staleTime: Infinity,
  });

  const variogramSlicesUrl = useBlobUrl(loadedVariogramSlicesImage.data?.data);
  const sphericalUrl = useBlobUrl(loadedSphericalImage.data?.data);
  const gaussianUrl = useBlobUrl(loadedGaussianImage.data?.data);
  const generalExponentialUrl = useBlobUrl(
    loadedGeneralExponentialImage.data?.data,
  );
  const exponentialUrl = useBlobUrl(loadedExponentialImage.data?.data);

  return (
    <Styled.Dialog open={open} isDismissable>
      <Dialog.CustomContent className="dialog-content">
        <Tabs activeTab={activeTab} onChange={handleChange} className="tabs">
          <Tabs.List>
            <Tabs.Tab>Variogram slice</Tabs.Tab>
            <Tabs.Tab>Spherical</Tabs.Tab>
            <Tabs.Tab>Gaussian</Tabs.Tab>
            <Tabs.Tab>General Exponential</Tabs.Tab>
            <Tabs.Tab>Exponential</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panels className="tabs-panels">
            <Tabs.Panel className="tabs-panel">
              <>
                {loadedVariogramSlicesImage.isLoading && (
                  <CenteredLoader size={24} />
                )}

                {loadedVariogramSlicesImage.data && (
                  <figure className="image-wrapper">
                    <img
                      className="image"
                      alt="Variogram x/y/z slice"
                      src={variogramSlicesUrl}
                    />
                  </figure>
                )}
              </>
            </Tabs.Panel>
            <Tabs.Panel className="tabs-panel">
              <>
                {loadedSphericalImage && loadedSphericalImage.data ? (
                  <>
                    {loadedSphericalImage.isLoading && (
                      <CenteredLoader size={24} />
                    )}
                    {loadedSphericalImage.data && (
                      <figure className="image-wrapper">
                        <img
                          className="image"
                          alt="Spherical variogram (empirical and fitted)"
                          src={sphericalUrl}
                        />
                      </figure>
                    )}
                  </>
                ) : (
                  <figure className="image-wrapper">
                    <Typography
                      variant="body_short"
                      className="placeholder-text"
                    >
                      Spherical variogram model is not included in the result
                    </Typography>
                  </figure>
                )}
              </>
            </Tabs.Panel>
            <Tabs.Panel className="tabs-panel">
              <>
                {loadedGaussianImage && loadedGaussianImage.data ? (
                  <>
                    {loadedGaussianImage.isLoading && (
                      <CenteredLoader size={24} />
                    )}
                    {loadedGaussianImage.data && (
                      <figure className="image-wrapper">
                        <img
                          className="image"
                          alt="Gaussian variogram (empirical and fitted)"
                          src={gaussianUrl}
                        />
                      </figure>
                    )}
                  </>
                ) : (
                  <figure className="image-wrapper">
                    <Typography
                      variant="body_short"
                      className="placeholder-text"
                    >
                      Gaussian variogram model is not included in the result
                    </Typography>
                  </figure>
                )}
              </>
            </Tabs.Panel>

            <Tabs.Panel className="tabs-panel">
              <>
                {loadedGeneralExponentialImage &&
                loadedGeneralExponentialImage.data ? (
                  <>
                    {loadedGeneralExponentialImage.isLoading && (
                      <CenteredLoader size={24} />
                    )}
                    {loadedGeneralExponentialImage.data && (
                      <figure className="image-wrapper">
                        <img
                          className="image"
                          alt="General Exponential variogram (empirical and fitted)"
                          src={generalExponentialUrl}
                        />
                      </figure>
                    )}
                  </>
                ) : (
                  <figure className="image-wrapper">
                    <Typography
                      variant="body_short"
                      className="placeholder-text"
                    >
                      General exponential variogram model is not included in the
                      result
                    </Typography>
                  </figure>
                )}
              </>
            </Tabs.Panel>
            <Tabs.Panel className="tabs-panel">
              <>
                {loadedExponentialImage && loadedExponentialImage.data ? (
                  <>
                    {loadedExponentialImage.isLoading && (
                      <CenteredLoader size={24} />
                    )}
                    {loadedExponentialImage.data && (
                      <figure className="image-wrapper">
                        <img
                          className="image"
                          alt="Exponential variogram (empirical and fitted)"
                          src={exponentialUrl}
                        />
                      </figure>
                    )}
                  </>
                ) : (
                  <figure className="image-wrapper">
                    <Typography
                      variant="body_short"
                      className="placeholder-text"
                    >
                      Exponential variogram model is not included in the result
                    </Typography>
                  </figure>
                )}
              </>
            </Tabs.Panel>
          </Tabs.Panels>
        </Tabs>
      </Dialog.CustomContent>

      <Dialog.Actions>
        <Button variant="contained" onClick={() => setOpen(!open)}>
          Close this window
        </Button>
      </Dialog.Actions>
    </Styled.Dialog>
  );
};
